// Signed preview URLs of draft assets (M7 slice 3; spec/api-conventions.md section 21.3, "Draft assets are not public until
// a pinned release lists them"): the anonymous `/content/assets/sha256/…` path answers 404 for a hash no pinned release
// lists, so every preview of the dashboard asks `POST …/branches/{branch}/assets:sign` for ten-minute URLs instead. The
// ids requested in one render tick are batched into one call per branch (at most 200 ids a call), each URL is cached for
// nine minutes and re-signed when that runs out. The public path (`contentPath`) stays what a document or a product stores.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useParams } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { useOptionalGame } from '../shell/GameContext';
import type { ApiClient } from './client';
import { ops } from './operations';

export interface BranchCtx {
  client: ApiClient;
  org: string;
  game: string;
  branch: string;
}

/** How long a signed URL is used before it is signed again (the server's URLs live ten minutes). */
export const SIGNED_URL_TTL_MS = 9 * 60 * 1000;
/** After a failed `:sign`, the ids are asked again this much later. */
const RETRY_MS = 60 * 1000;
/** A URL this close to going stale is re-signed with the batch that is going out anyway. */
const RESIGN_AHEAD_MS = 30 * 1000;
const MAX_IDS = 200;

interface Entry {
  /** null: the API left the id out (not an asset of the branch, no content) or the call failed */
  url: string | null;
  /** when the entry stops being fresh (ms since the epoch) */
  staleAt: number;
}

interface Batch {
  ctx: BranchCtx;
  ids: Set<string>;
  waiters: (() => void)[];
}

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<void>>();
const batches = new Map<string, Batch>();
const listeners = new Set<() => void>();
let version = 0;

const branchKey = (ctx: Pick<BranchCtx, 'org' | 'game' | 'branch'>) => `${ctx.org}/${ctx.game}/${ctx.branch}`;
const entryKey = (ctx: Pick<BranchCtx, 'org' | 'game' | 'branch'>, id: string) => `${branchKey(ctx)}#${id}`;

function notify() {
  version += 1;
  for (const l of Array.from(listeners)) l();
}

function fresh(key: string, now: number): boolean {
  const e = cache.get(key);
  return e !== undefined && e.staleAt > now;
}

async function flush(key: string): Promise<void> {
  const batch = batches.get(key);
  batches.delete(key);
  if (!batch) return;
  const { ctx } = batch;
  // the URLs of this branch that run stale within the next half minute ride along, so the thumbnails of a page are
  // re-signed in one call rather than one call per thumbnail as their timers fire
  const soon = Date.now() + RESIGN_AHEAD_MS;
  const prefix = `${key}#`;
  let release: () => void = () => undefined;
  const extra = new Promise<void>((resolve) => (release = resolve));
  for (const [k, e] of cache) {
    if (!k.startsWith(prefix) || e.staleAt > soon || inflight.has(k)) continue;
    batch.ids.add(k.slice(prefix.length));
    inflight.set(k, extra);
  }
  batch.waiters.push(release);
  const ids = Array.from(batch.ids);
  for (let i = 0; i < ids.length; i += MAX_IDS) {
    const chunk = ids.slice(i, i + MAX_IDS);
    try {
      const r = await ctx.client.data(ops.signAssetUrls, { path: { org: ctx.org, game: ctx.game, branch: ctx.branch }, body: { assets: chunk } });
      const now = Date.now();
      const urls = r?.urls ?? {};
      for (const id of chunk) cache.set(entryKey(ctx, id), { url: urls[id]?.url ?? null, staleAt: now + SIGNED_URL_TTL_MS });
    } catch {
      const now = Date.now();
      for (const id of chunk) {
        const prior = cache.get(entryKey(ctx, id));
        // a URL signed earlier keeps working until its own ten minutes end; only a missing one becomes null
        cache.set(entryKey(ctx, id), { url: prior?.url ?? null, staleAt: now + RETRY_MS });
      }
    }
  }
  for (const id of ids) inflight.delete(entryKey(ctx, id));
  for (const w of batch.waiters) w();
  notify();
}

/** Asks for the ids whose URL is missing or stale; resolves when every one of them has an answer. */
export function requestAssetUrls(ctx: BranchCtx, ids: readonly string[], now = Date.now()): Promise<void> {
  const waits: Promise<void>[] = [];
  const key = branchKey(ctx);
  for (const id of new Set(ids)) {
    if (!id) continue;
    const k = entryKey(ctx, id);
    if (fresh(k, now)) continue;
    const running = inflight.get(k);
    if (running) {
      waits.push(running);
      continue;
    }
    let batch = batches.get(key);
    if (!batch) {
      batch = { ctx, ids: new Set(), waiters: [] };
      batches.set(key, batch);
      const b = batch;
      // one call per branch for everything asked in this tick (the effects of one commit run together)
      void Promise.resolve().then(() => {
        if (batches.get(key) === b) void flush(key);
      });
    }
    batch.ids.add(id);
    const b = batch;
    const p = new Promise<void>((resolve) => b.waiters.push(resolve));
    inflight.set(k, p);
    waits.push(p);
  }
  return Promise.all(waits).then(() => undefined);
}

/** The cached URL of an asset (fresh or not yet re-signed), or undefined. */
export function cachedAssetUrl(ctx: Pick<BranchCtx, 'org' | 'game' | 'branch'>, id: string): string | undefined {
  return cache.get(entryKey(ctx, id))?.url ?? undefined;
}

/** The signed URLs of these assets (a non-hook form, for the extension API's `fileUrl`). */
export async function signedAssetUrls(ctx: BranchCtx, ids: readonly string[]): Promise<Record<string, string | undefined>> {
  await requestAssetUrls(ctx, ids);
  const out: Record<string, string | undefined> = {};
  for (const id of ids) out[id] = cachedAssetUrl(ctx, id);
  return out;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The signed preview URLs of `ids` on a branch: `{ [id]: url }` with the ids whose URL is known (a missing one is still being
 * signed, or has no content). Null `ctx` asks nothing.
 */
export function useAssetUrls(ctx: BranchCtx | null, ids: readonly (string | null | undefined)[]): Record<string, string | undefined> {
  const v = useSyncExternalStore(subscribe, () => version);
  const [tick, setTick] = useState(0);
  const wantedKey = Array.from(new Set(ids.filter((x): x is string => typeof x === 'string' && x.length > 0)))
    .sort()
    .join(' ');
  const wanted = useMemo(() => (wantedKey ? wantedKey.split(' ') : []), [wantedKey]);
  const ctxKey = ctx ? branchKey(ctx) : '';
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    const c = ctxRef.current;
    if (!c || wanted.length === 0) return;
    const now = Date.now();
    void requestAssetUrls(c, wanted, now);
    // re-sign when the first of these runs stale
    let next = Infinity;
    for (const id of wanted) {
      const e = cache.get(entryKey(c, id));
      if (e && e.staleAt > now) next = Math.min(next, e.staleAt);
    }
    if (next === Infinity) return;
    const timer = setTimeout(() => setTick((t) => t + 1), Math.max(0, next - now) + 5);
    return () => clearTimeout(timer);
  }, [ctxKey, wanted, v, tick]);

  const out: Record<string, string | undefined> = {};
  if (ctx) for (const id of wanted) out[id] = cachedAssetUrl(ctx, id);
  return out;
}

/** The branch of the current page as a signing context (null outside a game or a branch). */
export function useBranchAssetCtx(): BranchCtx | null {
  const { client } = useAuth();
  const gameCtx = useOptionalGame();
  const { branch } = useParams();
  return useMemo(() => (gameCtx && branch ? { client, org: gameCtx.org, game: gameCtx.game.slug, branch } : null), [client, gameCtx, branch]);
}

/** Forgets every cached URL (the tests, between cases). */
export function resetAssetUrls(): void {
  cache.clear();
  inflight.clear();
  batches.clear();
  version += 1;
}
